/*
PW Error Indicator

Normalized component-level error status indicator

Options (props)
- error: `null` indicating no error | `string` or `object` indicating an error, and conveying its message

example object:
```
  error: {
    heading: "Error heading"
    body: "Main body"
    additional: [
      "- additional item"
    ]
  }
```
*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import cx from 'classnames'
import ScrollBars from './scrollbars/ScrollBars'

const _getSoftErrorMessage = statusCode => {
  const out = {
    heading: 'Something went wrong',
    body:
      'Sorry, We were unable to complete the requested action. Please try again soon.'
  }
  if (statusCode != null && statusCode === 403) {
    out.heading = 'Resource denied'
    out.body = 'Sorry, you do not have permissions to perform this action.'
  }
  if (statusCode != null && statusCode === 404) {
    out.heading = 'Resource not found'
    out.body = 'Sorry, we were unable to locate the requested file or resource.'
  }

  return out
}

class ErrorState extends React.Component {
  static displayName = 'ErrorState'

  static propTypes = {
    soft: T.bool,
    error: T.oneOfType([
      T.string,
      T.shape({
        type: T.string,
        heading: T.string,
        body: T.string,
        additional: T.array,
        statusCode: T.number
      })
    ]),
    customMessage: T.string,
    className: T.string
  }

  static contextTypes = {
    // An ancestor can pass this down in its getChildContext to default all ErrorState
    // instances to soft errors, as an alternative to passing 'soft' in the props.
    errorStateDefaultSoft: T.bool,
    userIsSupportAccount: T.bool,
    renderTraceURL: T.func
  }

  _renderTraceId = () => {
    const { error } = this.props
    if (error && error.traceId && error.traceId.length > 0) {
      return (
        <div className="trace_id">
          Request ID:{' '}
          {this.context.userIsSupportAccount && this.context.renderTraceURL
            ? <a
                className="trace_id_number"
                href={this.context.renderTraceURL(error.traceId)}
                target="_blank"
              >
                {error.traceId}
              </a>
            : <span className="trace_id_number">
                {error.traceId}
              </span>}
        </div>
      )
    }
  }

  _renderError = () => {
    const error = this.props.error
    return _.isObject(error)
      ? <div>
          <h2>
            {error.heading}
          </h2>
          <div className="relative_wrap">
            <ScrollBars>
              <p>
                {error.body}
              </p>
              {_.map(error.additional || [], itm =>
                <p key={itm}>
                  {itm}
                </p>
              )}
            </ScrollBars>
          </div>
        </div>
      : <p>
          {error}
        </p>
  }

  _renderSoftError = () => {
    const error = this.props.error
    if (_.isObject(error)) {
      const _msg = _getSoftErrorMessage(error.statusCode)
      return (
        <div>
          <h2>
            {_msg.heading}
          </h2>
          <div className="relative_wrap">
            <p>
              {_msg.body}
            </p>
          </div>
        </div>
      )
    } else {
      return (
        <p>
          {error}
        </p>
      )
    }
  }

  render() {
    const props = this.props
    const isSoftError =
      props.soft || (props.soft == null && this.context.errorStateDefaultSoft)

    if (props.customMessage) {
      return (
        <div
          className={cx(
            'custom_error_indicator',
            { active: props.error != null },
            props.className
          )}
        >
          <div>
            {props.customMessage}
            {this._renderTraceId()}
          </div>
        </div>
      )
    } else {
      return (
        <div
          className={cx(
            'error_indicator',
            {
              active: props.error != null,
              soft: isSoftError
            },
            props.error && props.error.type,
            props.className
          )}
        >
          <div>
            <span className="error_icon" />
            {isSoftError ? this._renderSoftError() : this._renderError()}
            {this._renderTraceId()}
          </div>
        </div>
      )
    }
  }
}

export default ErrorState
